import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { MitmRevealConfig, ScreenBox } from "../scenario/types";
import { audio } from "../lib/audio";
import { SkipButton } from "../ui/SkipButton";
import { tx } from "../lib/tx";

/**
 * Hacker-room reveal: an animated man-in-the-middle diagram PLUS a mirrored
 * replay of the exact steps the learner just took, and a live "credential
 * extraction" terminal feed — all narrated as the attacker's OWN self-talk
 * (he's talking through his evening to himself, not lecturing the learner in
 * 2nd person). Mirrors `ChargeExfil.tsx`'s "show the threat" cinematic
 * (tension audio bed, live progress, a `breachAlert` sting on completion).
 */
export function MitmReveal({
  config,
  onDone,
  onLineChange,
  screenBox,
}: {
  config: MitmRevealConfig;
  onDone: () => void;
  /** Reports the currently-spoken narration line index up to the parent, so
   *  the shared top-right storyboard card (GuideTooltip) can show the
   *  hacker's live monologue instead of a static beat blurb — all of this
   *  cinematic's "text" now lives there, off the on-screen visualization. */
  onLineChange?: (idx: number) => void;
  /** Projected screen-space box for the CRT monitor mesh (from the env's
   *  `screenQuad` via GltfScreenOverlay). When present + visible, the card is
   *  glued to the monitor by inline geometry — replaces the fragile,
   *  per-viewport hand-tuned `.sim-root[data-env="hacker_room"] .mitm-wrap`
   *  CSS that kept "not fitting / placed to the right" (2026-07-29, 6th try).
   *  Falls back to that CSS when null/not-visible so a miscalibrated quad can
   *  never blank the reveal. */
  screenBox?: ScreenBox | null;
}) {
  const [packetIdx, setPacketIdx] = useState(-1);
  const [lineIdx, setLineIdx] = useState(0);
  const [replayIdx, setReplayIdx] = useState(-1);
  const [termShown, setTermShown] = useState(0);
  const [ready, setReady] = useState(false);
  const started = useRef(false);
  const skipped = useRef(false);

  const victim = config.victimLabel ?? "Your laptop";
  const hotspot = config.hotspotLabel ?? "FREE-BAR-WIFI-SUPERFAST (his hotspot)";
  const target = config.targetLabel ?? "Internet / company systems";
  const replaySteps = config.replaySteps ?? [];
  const terminalLines = config.terminalLines ?? [];
  const hotspotAt = config.hotspotRevealAt ?? 0;
  const victimAt = config.victimRevealAt ?? 0;
  const targetAt = config.targetRevealAt ?? 0;
  const hotspotIn = lineIdx >= hotspotAt;
  const victimIn = lineIdx >= victimAt;
  const targetIn = lineIdx >= targetAt;

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    audio.startExfilAlarm();

    let cancelled = false;
    (async () => {
      const lang = config.language ?? "en-US";
      const voice = config.voiceId ?? "onyx";
      void Promise.all(config.narration.map((t) => audio.prefetchSpeech(t, { voice, lang })));
      const total = config.narration.length;
      for (let i = 0; i < total; i++) {
        if (cancelled || skipped.current) return;
        setLineIdx(i);
        onLineChange?.(i);
        // Advance the packet/replay/terminal choreography roughly in step
        // with narration (each capped to its own list length — a shorter
        // list just holds its last entry lit for any remaining lines).
        setPacketIdx(Math.min(i, config.packets.length - 1));
        if (replaySteps.length) setReplayIdx(Math.min(i, replaySteps.length - 1));
        if (terminalLines.length) {
          const frac = total > 1 ? i / (total - 1) : 1;
          setTermShown(Math.max(1, Math.round(frac * terminalLines.length)));
        }
        if (i > 0 && i - 1 < config.packets.length) audio.dataBlip();
        await audio.speak(config.narration[i], { voice, lang });
        if (cancelled || skipped.current) return;
        await new Promise((r) => window.setTimeout(r, 250));
      }
      if (cancelled || skipped.current) return;
      setTermShown(terminalLines.length);
      setReady(true);
      audio.stopExfilAlarm();
      audio.breachAlert();
    })();

    return () => {
      cancelled = true;
      audio.cancelSpeech();
      audio.stopExfilAlarm();
    };
  }, [config]);

  // Lets a learner who's got the point fast-forward past the (sometimes
  // lengthy) hacker monologue straight to the finished reveal, instead of
  // sitting through every narrated line — jumps every choreographed piece
  // (packets/replay/terminal/narration index) to its final state so the
  // on-screen result matches what a full playthrough would have ended on.
  const skip = () => {
    if (skipped.current) return;
    skipped.current = true;
    audio.cancelSpeech();
    audio.stopExfilAlarm();
    const lastLine = Math.max(0, config.narration.length - 1);
    setLineIdx(lastLine);
    onLineChange?.(lastLine);
    setPacketIdx(config.packets.length - 1);
    if (replaySteps.length) setReplayIdx(replaySteps.length - 1);
    setTermShown(terminalLines.length);
    setReady(true);
  };

  // Glue the card to the projected monitor box when the projector reports a
  // visible quad; otherwise let the (fallback) CSS position it. Inline styles
  // beat the CSS `left:50%/top:15.9vh/transform` overrides by specificity.
  const glued = !!(screenBox && screenBox.visible);
  const wrapStyle: CSSProperties | undefined = glued
    ? {
        left: `${screenBox!.leftPct}%`,
        top: `${screenBox!.topPct}%`,
        width: `${screenBox!.widthPct}%`,
        height: `${screenBox!.heightPct}%`,
        right: "auto",
        bottom: "auto",
        transform: "none",
        alignItems: "stretch",
        justifyContent: "stretch",
      }
    : undefined;

  return (
    <div
      className={`mitm-wrap ${glued ? "mitm-wrap--glued" : ""}`}
      style={wrapStyle}
      role="alertdialog"
      aria-label={tx("Man-in-the-middle interception")}
    >
      {/* The on-screen visualization ONLY — narration/room-label text all
          lives in the shared top-right storyboard card now (see App.tsx's
          GuideTooltip branch for kind === "mitmreveal"), so this panel can
          stay small enough to sit on the hacker's monitor without covering
          the WALK/LOOK HUD. */}
      <div className="mitm-card">
        <div className="mitm-head">
          <span className="mitm-dot" /> {tx("INTERCEPTED TRAFFIC")}
        </div>

        <div className="mitm-diagram" aria-hidden>
          <div className={`mitm-node mitm-node--victim ${victimIn ? "in" : "wait"}`}>
            <span className="mitm-node-ico">{"\u{1F4BB}"}</span>
            <span className="mitm-node-label">{victim}</span>
          </div>
          <div className={`mitm-link mitm-link--a ${victimIn ? "in" : "wait"}`}>
            {config.packets.map((p, i) => (
              <span key={p} className={`mitm-packet ${i === packetIdx ? "run" : i < packetIdx ? "past" : ""}`}>
                {p}
              </span>
            ))}
          </div>
          <div className={`mitm-node mitm-node--hotspot ${hotspotIn ? "in" : "wait"}`}>
            <span className="mitm-node-ico">{"\u{1F977}"}</span>
            <span className="mitm-node-label">{hotspot}</span>
            <span className="mitm-node-sub">{tx("sits on every packet you send")}</span>
          </div>
          <div className={`mitm-link mitm-link--b ${targetIn ? "in" : "wait"}`}>
            {config.packets.map((p, i) => (
              <span
                key={p}
                className={`mitm-packet ${i === packetIdx ? "run run--out" : i < packetIdx ? "past" : ""}`}
              >
                {p}
              </span>
            ))}
          </div>
          <div className={`mitm-node mitm-node--target ${targetIn ? "in" : "wait"}`}>
            <span className="mitm-node-ico">{"\u{1F3E2}"}</span>
            <span className="mitm-node-label">{target}</span>
          </div>
        </div>

        {(replaySteps.length > 0 || terminalLines.length > 0) && (
          <div className="mitm-screens" aria-hidden>
            {replaySteps.length > 0 && (
              <div className="mitm-replay">
                <div className="mitm-panel-label">{tx("His screen")}</div>
                <ul className="mitm-replay-list">
                  {replaySteps.map((s, i) => (
                    <li
                      key={s}
                      className={i === replayIdx ? "active" : i < replayIdx ? "past" : ""}
                    >
                      <span className="mitm-replay-dot" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {terminalLines.length > 0 && (
              <div className="mitm-term">
                <div className="mitm-panel-label">{tx("Terminal")}</div>
                <div className="mitm-term-body">
                  {terminalLines.slice(0, termShown).map((l, i) => (
                    <div key={i} className="mitm-term-line">
                      {l}
                    </div>
                  ))}
                  <span className="mitm-term-cursor">{"\u2588"}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="cq-continue-group mitm-continue-group">
          <button type="button" className="btn mitm-continue" disabled={!ready} onClick={onDone}>
            {ready ? config.continueLabel ?? tx("Continue") : "\u2026"}
          </button>
          {!ready && <SkipButton onClick={skip} label={tx("Skip explanation")} />}
        </div>
      </div>
    </div>
  );
}
